import axios from 'axios';
import toast from 'react-hot-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Create API instance for vital signs storage
export const vitalSignsStorageAPI = axios.create({
  baseURL: `${BACKEND_URL}/api/vital-signs`,
  timeout: 800000, // 800 second timeout for file uploads
});

// Request interceptor
vitalSignsStorageAPI.interceptors.request.use(
  (config) => {
    console.log('📡 Vital Signs Storage API request:', config.method?.toUpperCase(), config.url);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
vitalSignsStorageAPI.interceptors.response.use(
  (response) => {
    console.log('✅ Vital Signs Storage API response:', response.status, response.config.url);
    return response;
  },
  (error) => {
    console.error('❌ Vital Signs Storage API error:', error.response?.status, error.config?.url);
    console.error('Error details:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message
    });
    return Promise.reject(error);
  }
);

export const vitalSignsAPI = {
  // Upload video recording (Step 1)
  uploadVideoRecording: (sessionId, videoBlob, metadata = {}) => {
    console.log('=== FRONTEND: vitalSignsAPI.uploadVideoRecording called ===');
    console.log('Session ID:', sessionId);
    console.log('Video blob size:', videoBlob ? videoBlob.size : 'No blob');
    console.log('Metadata:', metadata);
    
    console.log('✅ Upload proceeding - blob size:', videoBlob ? videoBlob.size : 'undefined', 'bytes');
    
    try {
      const formData = new FormData();
      // Use actual blob type instead of hardcoding WebM
      const actualType = videoBlob.type || 'video/webm';
      const extension = actualType.includes('mp4') ? 'mp4' : 'webm';
      const videoFile = new File([videoBlob], `video_${sessionId}_${Date.now()}.${extension}`, { 
        type: actualType 
      });
      
      console.log('🎯 Smart format detection - Video blob type:', actualType, 'Extension:', extension);
      console.log('Video file created:', videoFile.name, 'Size:', videoFile.size, 'Type:', videoFile.type);
      
      formData.append('video_file', videoFile);
      formData.append('session_id', sessionId);
      formData.append('recording_type', 'video');
      formData.append('metadata', JSON.stringify({
        ...metadata,
        timestamp: new Date().toISOString(),
        duration: metadata.duration || 0,
        step: 1
      }));
      
      console.log('Form data prepared, making API call...');
      return vitalSignsStorageAPI.post('/upload-video', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    } catch (error) {
      console.error('=== FRONTEND: vitalSignsAPI.uploadVideoRecording failed ===');
      console.error('Error:', error);
      console.error('Session ID:', sessionId);
      console.error('Video blob size:', videoBlob ? videoBlob.size : 'No blob');
      throw error;
    }
  },

  // Upload pulse audio recording (Step 2)
  uploadPulseAudio: (sessionId, audioBlob, metadata = {}) => {
    console.log('=== FRONTEND: vitalSignsAPI.uploadPulseAudio called ===');
    console.log('Session ID:', sessionId);
    console.log('Audio blob size:', audioBlob ? audioBlob.size : 'No blob');
    console.log('Metadata:', metadata);
    
    try {
      const formData = new FormData();
      // Use actual blob type instead of hardcoding WebM
      const actualType = audioBlob.type || 'audio/webm';
      const extension = actualType.includes('mp4') || actualType.includes('m4a') ? (actualType.includes('m4a') ? 'm4a' : 'mp4') : 'webm';
      const audioFile = new File([audioBlob], `pulse_${sessionId}_${Date.now()}.${extension}`, { 
        type: actualType 
      });
      
      console.log('🎯 Smart format detection - Pulse audio blob type:', actualType, 'Extension:', extension);
      console.log('Audio file created:', audioFile.name, 'Size:', audioFile.size, 'Type:', audioFile.type);
      
      formData.append('audio_file', audioFile);
      formData.append('session_id', sessionId);
      formData.append('recording_type', 'pulse_audio');
      formData.append('metadata', JSON.stringify({
        ...metadata,
        timestamp: new Date().toISOString(),
        duration: metadata.duration || 0,
        step: 2,
        audio_analysis: metadata.audioAnalysis || {}
      }));
      
      console.log('Form data prepared, making API call...');
      return vitalSignsStorageAPI.post('/upload-pulse-audio', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    } catch (error) {
      console.error('=== FRONTEND: vitalSignsAPI.uploadPulseAudio failed ===');
      console.error('Error:', error);
      console.error('Session ID:', sessionId);
      console.error('Audio blob size:', audioBlob ? audioBlob.size : 'No blob');
      throw error;
    }
  },

  // Upload vowel audio recording (Step 3)
  uploadVowelAudio: (sessionId, audioBlob, metadata = {}) => {
    const formData = new FormData();
    // Use actual blob type instead of hardcoding WebM
    const actualType = audioBlob.type || 'audio/webm';
    const extension = actualType.includes('mp4') || actualType.includes('m4a') ? (actualType.includes('m4a') ? 'm4a' : 'mp4') : 'webm';
    const audioFile = new File([audioBlob], `vowel_${sessionId}_${Date.now()}.${extension}`, { 
      type: actualType 
    });
    
    console.log('🎯 Smart format detection - Vowel audio blob type:', actualType, 'Extension:', extension);
    console.log('Vowel audio file created:', audioFile.name, 'Size:', audioFile.size, 'Type:', audioFile.type);
    
    formData.append('audio_file', audioFile);
    formData.append('session_id', sessionId);
    formData.append('recording_type', 'vowel_audio');
    formData.append('metadata', JSON.stringify({
      ...metadata,
      timestamp: new Date().toISOString(),
      duration: metadata.duration || 0,
      step: 3,
      audio_analysis: metadata.audioAnalysis || {}
    }));
    
    return vitalSignsStorageAPI.post('/upload-vowel-audio', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  // Complete vital signs session
  completeVitalSignsSession: (sessionId, allRecordings = [], recordingRequestId = null, abortSignal = null) => {
    console.log('=== FRONTEND: vitalSignsAPI.completeVitalSignsSession called ===');
    console.log('Session ID:', sessionId);
    console.log('Recordings count:', allRecordings.length);
    console.log('Recording request ID:', recordingRequestId);
    console.log('Abort signal provided:', !!abortSignal);
    console.log('Recordings:', allRecordings.map(rec => ({ type: rec.type, duration: rec.duration })));
    
    try {
      const formData = new FormData();
      formData.append('session_id', sessionId);
      
      // Add recording request ID if provided
      if (recordingRequestId) {
        formData.append('recording_request_id', recordingRequestId);
      }
      
      const recordingsSummary = {
        total_recordings: allRecordings.length,
        recordings: allRecordings.map(rec => ({
          type: rec.type,
          file_path: rec.filePath,
          duration: rec.duration,
          timestamp: rec.timestamp,
          metadata: rec.metadata
        })),
        completed_at: new Date().toISOString()
      };
      
      formData.append('recordings_summary', JSON.stringify(recordingsSummary));
      
      console.log('Form data prepared, making API call...');
      console.log('Recordings summary:', recordingsSummary);
      
      // Build request config with timeout handling
      const requestConfig = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      };
      
      // Add abort signal if provided (for timeout handling)
      if (abortSignal) {
        requestConfig.signal = abortSignal;
        console.log('🎯 TIMEOUT PROTECTION: AbortController signal attached to request');
      }
      
      return vitalSignsStorageAPI.post('/complete-session', formData, requestConfig);
    } catch (error) {
      console.error('=== FRONTEND: vitalSignsAPI.completeVitalSignsSession failed ===');
      console.error('Error:', error);
      console.error('Session ID:', sessionId);
      console.error('Recordings count:', allRecordings.length);
      throw error;
    }
  },

  // Get session recordings
  getSessionRecordings: (sessionId) => {
    return vitalSignsStorageAPI.get(`/session/${sessionId}/recordings`);
  },

  // Get recording file
  getRecordingFile: (filePath) => {
    return vitalSignsStorageAPI.get(`/file/${filePath}`, {
      responseType: 'blob'
    });
  },

  // Delete recording
  deleteRecording: (sessionId, recordingId) => {
    return vitalSignsStorageAPI.delete(`/session/${sessionId}/recording/${recordingId}`);
  },

  // Get storage statistics
  getStorageStats: () => {
    return vitalSignsStorageAPI.get('/storage-stats');
  },

  // Save vitals summary
  saveSummary: (patientId, summary, recordingToken = null) => {
    console.log('🔍 DEBUG: saveSummary called with:', { patientId, summary, recordingToken: !!recordingToken });
    
    // If we have a recording token, use the recording-specific endpoint
    if (recordingToken) {
      console.log('🔗 Using recording token endpoint for vitals saving');
      const { tenantPatientAPI } = require('./tenantPatientAPI');
      return tenantPatientAPI.saveVitalsFromRecording(recordingToken, summary);
    }
    
    // Otherwise use the original patient-specific endpoint (for admin/staff use)
    const formData = new FormData();
    formData.append('session_id', summary.session_id);
    if (summary.hr != null) formData.append('hr', String(summary.hr));
    if (summary.hrv != null) formData.append('hrv', String(summary.hrv));
    if (summary.rhythm) formData.append('rhythm', summary.rhythm);
    if (summary.bp_sys != null) formData.append('bp_sys', String(summary.bp_sys));
    if (summary.bp_dia != null) formData.append('bp_dia', String(summary.bp_dia));
    if (summary.quality_flag != null) formData.append('quality_flag', String(summary.quality_flag));
    
    console.log('🔍 DEBUG: FormData prepared:', {
      session_id: summary.session_id,
      hr: summary.hr,
      hrv: summary.hrv,
      rhythm: summary.rhythm,
      bp_sys: summary.bp_sys,
      bp_dia: summary.bp_dia,
      quality_flag: summary.quality_flag
    });
    
    // Use the main API client for this endpoint since it's not part of vital-signs storage
    const { api } = require('../main-api/apiClient');
    console.log('🔍 DEBUG: Making API call to:', `/patients/${patientId}/vitals`);
    
    return api.post(`/patients/${patientId}/vitals`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

export default vitalSignsAPI;
